import { KEYUTIL } from 'jsrsasign';
import type {
  ElementType,
  HTMLAttributes,
  LegacyRef,
  MutableRefObject,
  PropsWithChildren,
  Ref,
  RefObject,
} from 'react';
import { useEffect } from 'react';

declare global {
  interface Window {
    loadPaymentScript: (id: string, props: Record<string, unknown>) => void;
  }
}

type BaseProps<T extends HTMLElement = HTMLElement> = HTMLAttributes<T> & {
  as?: ElementType;
  forwardRef?: Ref<T> | MutableRefObject<T | undefined> | RefObject<T>;
};

type InstructionProps = PropsWithChildren<BaseProps>;

type LabelProps = PropsWithChildren<
  BaseProps & {
    label?: string;
  }
>;

type ValueProps = PropsWithChildren<
  BaseProps & {
    value?: string | number;
  }
>;

type ScriptProps = BaseProps<HTMLDivElement> & {
  code: string;
  publicKey: string;
  signature: string;
  scriptProps?: Record<string, unknown>;
  onLoad?: () => void;
};

const hexToString = (hex: string): string => {
  let str = '';
  for (let i = 0; i < hex.length; i += 2) {
    str += String.fromCharCode(parseInt(hex.substring(i, i + 2), 16));
  }
  return decodeURIComponent(escape(str));
};

const validateSignature = (
  content: string,
  signature: string,
  publicKeyString: string,
): boolean => {
  if (!content) {
    return false;
  }
  const publicKey = KEYUTIL.getKey(publicKeyString) as {
    verify: (content: string, signature: string) => boolean;
  };
  return publicKey.verify(content, signature);
};

function Instruction({
  as = 'div',
  children,
  forwardRef,
  ...rest
}: InstructionProps) {
  const Element = as as ElementType;
  return (
    <Element className="Instruction" ref={forwardRef} {...rest}>
      {children}
    </Element>
  );
}

function Label({
  as = 'div',
  children,
  label,
  forwardRef,
  ...rest
}: LabelProps) {
  const Element = as as ElementType;
  return (
    <Element
      className="Instruction-label"
      data-testid="label"
      ref={forwardRef}
      {...rest}
    >
      {children || label}
    </Element>
  );
}

function Value({
  as = 'div',
  children,
  value,
  forwardRef,
  ...rest
}: ValueProps) {
  const Element = as as ElementType;
  const htmlProps = children
    ? {}
    : { dangerouslySetInnerHTML: { __html: String(value ?? '') } };

  return (
    <Element
      className="Instruction-value"
      data-testid="value"
      ref={forwardRef}
      {...htmlProps}
      {...rest}
    >
      {children}
    </Element>
  );
}

function Script({
  className,
  code,
  forwardRef,
  id = 'payment',
  onLoad,
  publicKey,
  signature,
  scriptProps = {},
  ...rest
}: ScriptProps) {
  useEffect(() => {
    if (!code || !signature || !publicKey) {
      return;
    }

    let codeToRun: { js: string };
    try {
      codeToRun = JSON.parse(hexToString(code));
    } catch {
      return;
    }

    if (validateSignature(code, signature, publicKey)) {
      // biome-ignore lint/security/noGlobalEval: Required for embedded payment scripts
      eval(codeToRun.js);
      window.loadPaymentScript('payment', scriptProps);
      onLoad?.();
    }
  }, [code, signature, publicKey, scriptProps, onLoad]);

  return (
    <div
      id={id}
      className={className}
      ref={forwardRef as LegacyRef<HTMLDivElement>}
      {...rest}
    />
  );
}

export const Instructions = {
  Instruction,
  Label,
  Value,
  Script,
};
